import type { WalletStats } from "./wallet-card-content";

type StickerMetric =
  | "events"
  | "hiives"
  | "posts"
  | "comments"
  | "reviews"
  | "shop"
  | "venues"
  | "buddies"
  | "sessionsCreated"
  | "sessionsJoined"
  | "gamesPlayed"
  | "wins"
  | "rating"
  | "personalSessions"
  | "chatMessages"
  | "stories"
  | "workouts"
  | "runs"
  | "earlyBookings"
  | "lateEvents"
  | "packs"
  | "invites"
  | "distanceKm"
  | "hexes"
  | "streakDays"
  | "founding"
  | "likesReceived"
  | "polls"
  | "scoresEntered"
  | "ratingGain"
  | "sports"
  | "fullHouses"
  | "sessionInvites"
  | "splitsSettled"
  | "topups"
  | "hiiveMoneyPayments";

interface StickerPalette {
  background: string;
  foreground: string;
  accent: string;
}

/**
 * A sticker's drawing is `STICKER_ART[id]` (`./sticker-art`); the palette is
 * the glow its card throws, matched to the drawing's main colour.
 */
export interface WalletSticker {
  id: string;
  title: string;
  description: string;
  metric: StickerMetric;
  threshold: number;
  palette: StickerPalette;
}

export const STICKER_REGISTRY: WalletSticker[] = [
  {
    id: "event-1",
    title: "First Buzz",
    description: "You showed up for your first Hiive event.",
    metric: "events",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "event-5",
    title: "On The Move",
    description: "You made five Hiive events part of your story.",
    metric: "events",
    threshold: 5,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "event-10",
    title: "Full Flame",
    description: "You have checked in to ten Hiive events.",
    metric: "events",
    threshold: 10,
    palette: {
      background: "linear-gradient(145deg, #ffc9b8, #ff7a59)",
      foreground: "#7c2d12",
      accent: "#fff1ec",
    },
  },
  {
    id: "event-25",
    title: "Main Character",
    description: "You have been part of twenty-five Hiive events.",
    metric: "events",
    threshold: 25,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "hiive-1",
    title: "Found The Hive",
    description: "You joined your first Hiive community.",
    metric: "hiives",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "hiive-3",
    title: "Well Connected",
    description: "You are now part of three Hiive communities.",
    metric: "hiives",
    threshold: 3,
    palette: {
      background: "linear-gradient(145deg, #c9b6ff, #6a33ff)",
      foreground: "#2e1065",
      accent: "#efe9ff",
    },
  },
  {
    id: "post-10",
    title: "Conversation Starter",
    description: "You shared ten posts with your Hiive communities.",
    metric: "posts",
    threshold: 10,
    palette: {
      background: "linear-gradient(145deg, #cfe9ff, #7cc4ff)",
      foreground: "#0c4a6e",
      accent: "#eef7ff",
    },
  },
  {
    id: "post-50",
    title: "Community Voice",
    description: "You have shared fifty posts with the community.",
    metric: "posts",
    threshold: 50,
    palette: {
      background: "linear-gradient(145deg, #ffc9b8, #ff7a59)",
      foreground: "#7c2d12",
      accent: "#fff1ec",
    },
  },
  {
    id: "review-1",
    title: "First Star",
    description: "You reviewed your first venue or hiive.",
    metric: "reviews",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "review-5",
    title: "Reviewer Pro",
    description: "You shared five community reviews.",
    metric: "reviews",
    threshold: 5,
    palette: {
      background: "linear-gradient(145deg, #c9b6ff, #6a33ff)",
      foreground: "#2e1065",
      accent: "#efe9ff",
    },
  },
  {
    id: "shop-1",
    title: "First Purchase",
    description: "You bought your first item from a shop.",
    metric: "shop",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #c6f2e0, #5fd3a6)",
      foreground: "#14532d",
      accent: "#ecfbf4",
    },
  },
  {
    id: "shop-5",
    title: "Shopaholic",
    description: "You placed five shop orders.",
    metric: "shop",
    threshold: 5,
    palette: {
      background: "linear-gradient(145deg, #c6f2e0, #5fd3a6)",
      foreground: "#14532d",
      accent: "#ecfbf4",
    },
  },
  {
    id: "venue-1",
    title: "First Booking",
    description: "You made your first booking at a venue.",
    metric: "venues",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #ffc9b8, #ff7a59)",
      foreground: "#7c2d12",
      accent: "#fff1ec",
    },
  },
  {
    id: "venue-5",
    title: "Venue Explorer",
    description: "You made five venue bookings.",
    metric: "venues",
    threshold: 5,
    palette: {
      background: "linear-gradient(145deg, #cfe9ff, #7cc4ff)",
      foreground: "#0c4a6e",
      accent: "#eef7ff",
    },
  },
  {
    id: "comment-5",
    title: "Conversation Spark",
    description: "You posted five comments.",
    metric: "comments",
    threshold: 5,
    palette: {
      background: "linear-gradient(145deg, #ebe3ff, #c9b6ff)",
      foreground: "#4a2bb3",
      accent: "#f5f1ff",
    },
  },
  {
    id: "comment-20",
    title: "Social Buzz",
    description: "You shared twenty comments with the community.",
    metric: "comments",
    threshold: 20,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "buddy-1",
    title: "Buddy Up",
    description: "You made your first buddy connection.",
    metric: "buddies",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #ffd9ea, #ff9ec7)",
      foreground: "#831843",
      accent: "#fff0f6",
    },
  },
  {
    id: "buddy-5",
    title: "Squad Coordinator",
    description: "You connected with five buddies.",
    metric: "buddies",
    threshold: 5,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "session-created-1",
    title: "Session Starter",
    description: "You hosted your first activity session.",
    metric: "sessionsCreated",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "session-created-5",
    title: "Match Maker",
    description: "You hosted five activity sessions.",
    metric: "sessionsCreated",
    threshold: 5,
    palette: {
      background: "linear-gradient(145deg, #ffc9b8, #ff7a59)",
      foreground: "#7c2d12",
      accent: "#fff1ec",
    },
  },
  {
    id: "session-joined-1",
    title: "Active Player",
    description: "You joined your first activity session.",
    metric: "sessionsJoined",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #edf8c2, #c8e65a)",
      foreground: "#3f4d0b",
      accent: "#f7fde6",
    },
  },
  {
    id: "session-joined-10",
    title: "Game Regular",
    description: "You joined ten activity sessions.",
    metric: "sessionsJoined",
    threshold: 10,
    palette: {
      background: "linear-gradient(145deg, #ebe3ff, #c9b6ff)",
      foreground: "#4a2bb3",
      accent: "#f5f1ff",
    },
  },
  {
    id: "wins-1",
    title: "First Victory",
    description: "You won your first competitive match.",
    metric: "wins",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "wins-5",
    title: "Champion",
    description: "You won five competitive matches.",
    metric: "wins",
    threshold: 5,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "games-played-5",
    title: "Challenger",
    description: "You played five competitive matches.",
    metric: "gamesPlayed",
    threshold: 5,
    palette: {
      background: "linear-gradient(145deg, #ffc9b8, #ff7a59)",
      foreground: "#7c2d12",
      accent: "#fff1ec",
    },
  },
  {
    id: "games-played-25",
    title: "Battle Tested",
    description: "You played twenty-five competitive matches.",
    metric: "gamesPlayed",
    threshold: 25,
    palette: {
      background: "linear-gradient(145deg, #c9b6ff, #6a33ff)",
      foreground: "#2e1065",
      accent: "#efe9ff",
    },
  },
  {
    id: "rating-1200",
    title: "Elite Competitor",
    description: "You reached a competitive rating of 1200.",
    metric: "rating",
    threshold: 1200,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "noncomp-5",
    title: "Dedicated Builder",
    description: "You completed five personal progress sessions.",
    metric: "personalSessions",
    threshold: 5,
    palette: {
      background: "linear-gradient(145deg, #cfe9ff, #7cc4ff)",
      foreground: "#0c4a6e",
      accent: "#eef7ff",
    },
  },
  {
    id: "chat-50",
    title: "Chatty Bee",
    description: "You sent fifty chat messages.",
    metric: "chatMessages",
    threshold: 50,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "chat-200",
    title: "Vocal Member",
    description: "You sent two hundred chat messages.",
    metric: "chatMessages",
    threshold: 200,
    palette: {
      background: "linear-gradient(145deg, #ebe3ff, #c9b6ff)",
      foreground: "#4a2bb3",
      accent: "#f5f1ff",
    },
  },
  {
    id: "story-3",
    title: "Storyteller",
    description: "You posted three stories.",
    metric: "stories",
    threshold: 3,
    palette: {
      background: "linear-gradient(145deg, #ffd9ea, #ff9ec7)",
      foreground: "#831843",
      accent: "#fff0f6",
    },
  },
  {
    id: "story-15",
    title: "Content Creator",
    description: "You posted fifteen stories.",
    metric: "stories",
    threshold: 15,
    palette: {
      background: "linear-gradient(145deg, #c9b6ff, #6a33ff)",
      foreground: "#2e1065",
      accent: "#efe9ff",
    },
  },
  {
    id: "workout-1",
    title: "First Lift",
    description: "You logged your first weight training workout.",
    metric: "workouts",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #ffc9b8, #ff7a59)",
      foreground: "#7c2d12",
      accent: "#fff1ec",
    },
  },
  {
    id: "workout-10",
    title: "Iron Addict",
    description: "You logged ten weight training workouts.",
    metric: "workouts",
    threshold: 10,
    palette: {
      background: "linear-gradient(145deg, #c9b6ff, #6a33ff)",
      foreground: "#2e1065",
      accent: "#efe9ff",
    },
  },
  {
    id: "movement-1",
    title: "First Step",
    description: "You logged your first run, walk, or cycle.",
    metric: "runs",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #c6f2e0, #5fd3a6)",
      foreground: "#14532d",
      accent: "#ecfbf4",
    },
  },
  {
    id: "movement-10",
    title: "Endurance Explorer",
    description: "You logged ten runs, walks, or cycles.",
    metric: "runs",
    threshold: 10,
    palette: {
      background: "linear-gradient(145deg, #cfe9ff, #7cc4ff)",
      foreground: "#0c4a6e",
      accent: "#eef7ff",
    },
  },
  // Appended, never inserted: a sticker's serial is its place in this list.
  {
    id: "event-50",
    title: "Hall of Fame",
    description: "You have been part of fifty Hiive events.",
    metric: "events",
    threshold: 50,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "hiive-5",
    title: "Hive Hopper",
    description: "You are part of five Hiive communities.",
    metric: "hiives",
    threshold: 5,
    palette: {
      background: "linear-gradient(145deg, #c9b6ff, #6a33ff)",
      foreground: "#2e1065",
      accent: "#efe9ff",
    },
  },
  {
    id: "buddy-15",
    title: "The Swarm",
    description: "You connected with fifteen buddies.",
    metric: "buddies",
    threshold: 15,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "wins-25",
    title: "Dynasty",
    description: "You won twenty-five competitive matches.",
    metric: "wins",
    threshold: 25,
    palette: {
      background: "linear-gradient(145deg, #c9b6ff, #6a33ff)",
      foreground: "#2e1065",
      accent: "#efe9ff",
    },
  },
  {
    id: "movement-50",
    title: "Long Haul",
    description: "You logged fifty runs, walks, or rides.",
    metric: "runs",
    threshold: 50,
    palette: {
      background: "linear-gradient(145deg, #c6f2e0, #5fd3a6)",
      foreground: "#14532d",
      accent: "#ecfbf4",
    },
  },
  {
    id: "workout-50",
    title: "Iron Habit",
    description: "You logged fifty weight training workouts.",
    metric: "workouts",
    threshold: 50,
    palette: {
      background: "linear-gradient(145deg, #ffc9b8, #ff7a59)",
      foreground: "#7c2d12",
      accent: "#fff1ec",
    },
  },
  {
    id: "early-bee-3",
    title: "Early Bee",
    description: "You booked three events at least a week ahead.",
    metric: "earlyBookings",
    threshold: 3,
    palette: {
      background: "linear-gradient(145deg, #ffc9b8, #ff7a59)",
      foreground: "#7c2d12",
      accent: "#fff1ec",
    },
  },
  {
    id: "night-shift-3",
    title: "Night Shift",
    description: "You checked in to three events starting at 9pm or later.",
    metric: "lateEvents",
    threshold: 3,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "pack-1",
    title: "Pack Leader",
    description: "You bought your first pack.",
    metric: "packs",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #c9b6ff, #6a33ff)",
      foreground: "#2e1065",
      accent: "#efe9ff",
    },
  },
  {
    id: "invite-1",
    title: "Plus One",
    description: "A friend you invited joined Hiive.",
    metric: "invites",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #ffc9b8, #ff7a59)",
      foreground: "#7c2d12",
      accent: "#fff1ec",
    },
  },
  {
    id: "distance-100",
    title: "Trailblazer",
    description: "You covered 100 km across your runs, walks, and rides.",
    metric: "distanceKm",
    threshold: 100,
    palette: {
      background: "linear-gradient(145deg, #ffc9b8, #ff7a59)",
      foreground: "#7c2d12",
      accent: "#fff1ec",
    },
  },
  {
    id: "hexes-10",
    title: "Land Grab",
    description: "You hold ten territory hexes.",
    metric: "hexes",
    threshold: 10,
    palette: {
      background: "linear-gradient(145deg, #c6f2e0, #5fd3a6)",
      foreground: "#14532d",
      accent: "#ecfbf4",
    },
  },
  {
    id: "streak-7",
    title: "Seven Straight",
    description: "You moved seven days in a row.",
    metric: "streakDays",
    threshold: 7,
    palette: {
      background: "linear-gradient(145deg, #ffc9b8, #ff7a59)",
      foreground: "#7c2d12",
      accent: "#fff1ec",
    },
  },
  {
    id: "founding-1",
    title: "Founding Bee",
    description: "You joined a Hiive in its first thirty days.",
    metric: "founding",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #c9b6ff, #6a33ff)",
      foreground: "#2e1065",
      accent: "#efe9ff",
    },
  },
  {
    id: "likes-50",
    title: "Crowd Pleaser",
    description: "Your posts collected fifty likes.",
    metric: "likesReceived",
    threshold: 50,
    palette: {
      background: "linear-gradient(145deg, #ffd9ea, #ff9ec7)",
      foreground: "#831843",
      accent: "#fff0f6",
    },
  },
  {
    id: "poll-1",
    title: "Hot Take",
    description: "You asked the community with your first poll.",
    metric: "polls",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #c9b6ff, #6a33ff)",
      foreground: "#2e1065",
      accent: "#efe9ff",
    },
  },
  {
    id: "score-1",
    title: "Scorekeeper",
    description: "You entered the score for a session.",
    metric: "scoresEntered",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "score-10",
    title: "Head Referee",
    description: "You entered the scores for ten sessions.",
    metric: "scoresEntered",
    threshold: 10,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "rating-gain-25",
    title: "Giant Slayer",
    description: "You gained twenty-five rating points in a single match.",
    metric: "ratingGain",
    threshold: 25,
    palette: {
      background: "linear-gradient(145deg, #c6f2e0, #5fd3a6)",
      foreground: "#14532d",
      accent: "#ecfbf4",
    },
  },
  {
    id: "sports-3",
    title: "All-Rounder",
    description: "You played sessions in three different sports.",
    metric: "sports",
    threshold: 3,
    palette: {
      background: "linear-gradient(145deg, #edf8c2, #c8e65a)",
      foreground: "#3f4d0b",
      accent: "#f7fde6",
    },
  },
  {
    id: "full-house-1",
    title: "Full House",
    description: "A session you hosted filled every spot.",
    metric: "fullHouses",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #ffc9b8, #ff7a59)",
      foreground: "#7c2d12",
      accent: "#fff1ec",
    },
  },
  {
    id: "session-invites-5",
    title: "Rally Call",
    description: "Five friends accepted your session invites.",
    metric: "sessionInvites",
    threshold: 5,
    palette: {
      background: "linear-gradient(145deg, #cfe9ff, #7cc4ff)",
      foreground: "#0c4a6e",
      accent: "#eef7ff",
    },
  },
  {
    id: "split-settled-1",
    title: "Settled Up",
    description: "You settled your share of a session split.",
    metric: "splitsSettled",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #c6f2e0, #5fd3a6)",
      foreground: "#14532d",
      accent: "#ecfbf4",
    },
  },
  {
    id: "topup-1",
    title: "Topped Up",
    description: "You added money to Hiive Money.",
    metric: "topups",
    threshold: 1,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
  {
    id: "hiive-money-3",
    title: "Honey Pot",
    description: "You paid with Hiive Money three times.",
    metric: "hiiveMoneyPayments",
    threshold: 3,
    palette: {
      background: "linear-gradient(145deg, #fde68a, #f6b938)",
      foreground: "#713f12",
      accent: "#fff7d6",
    },
  },
];

const METRIC_LABEL: Record<StickerMetric, [singular: string, plural: string]> =
  {
    events: ["event", "events"],
    hiives: ["Hiive", "Hiives"],
    posts: ["post", "posts"],
    comments: ["comment", "comments"],
    reviews: ["review", "reviews"],
    shop: ["order", "orders"],
    venues: ["venue booking", "venue bookings"],
    buddies: ["buddy connection", "buddy connections"],
    sessionsCreated: ["session created", "sessions created"],
    sessionsJoined: ["session joined", "sessions joined"],
    gamesPlayed: ["competitive match", "competitive matches"],
    wins: ["win", "wins"],
    rating: ["rating point", "rating points"],
    personalSessions: ["personal session", "personal sessions"],
    chatMessages: ["message", "messages"],
    stories: ["story", "stories"],
    workouts: ["workout logged", "workouts logged"],
    runs: ["run/walk logged", "runs/walks logged"],
    earlyBookings: ["event booked a week ahead", "events booked a week ahead"],
    lateEvents: ["late-night event", "late-night events"],
    packs: ["pack bought", "packs bought"],
    invites: ["invited friend joined", "invited friends joined"],
    distanceKm: ["km covered", "km covered"],
    hexes: ["territory hex held", "territory hexes held"],
    streakDays: ["day in a row", "days in a row"],
    founding: ["founding membership", "founding memberships"],
    likesReceived: ["like on your posts", "likes on your posts"],
    polls: ["poll", "polls"],
    scoresEntered: ["score entered", "scores entered"],
    ratingGain: ["rating point in one match", "rating points in one match"],
    sports: ["sport played", "sports played"],
    fullHouses: ["full session hosted", "full sessions hosted"],
    sessionInvites: ["session invite accepted", "session invites accepted"],
    splitsSettled: ["split settled", "splits settled"],
    topups: ["top-up", "top-ups"],
    hiiveMoneyPayments: ["Hiive Money payment", "Hiive Money payments"],
  };

function metricValue(stats: WalletStats, metric: StickerMetric): number {
  switch (metric) {
    case "events":
      return stats.eventCount;
    case "hiives":
      return stats.membershipCount;
    case "posts":
      return stats.postCount;
    case "comments":
      return stats.commentCount ?? 0;
    case "reviews":
      return stats.reviewCount ?? 0;
    case "shop":
      return stats.shopOrderCount ?? 0;
    case "venues":
      return stats.venueCount ?? 0;
    case "buddies":
      return stats.buddyCount ?? 0;
    case "sessionsCreated":
      return stats.sessionsCreatedCount ?? 0;
    case "sessionsJoined":
      return stats.sessionsJoinedCount ?? 0;
    case "gamesPlayed":
      return stats.gamesPlayedCount ?? 0;
    case "wins":
      return stats.winsCount ?? 0;
    case "rating":
      return stats.highRating ?? 1000;
    case "personalSessions":
      return stats.personalSessionsCount ?? 0;
    case "chatMessages":
      return stats.chatMessagesCount ?? 0;
    case "stories":
      return stats.storyCount ?? 0;
    case "workouts":
      return stats.workoutsLoggedCount ?? 0;
    case "runs":
      return stats.runsLoggedCount ?? 0;
    case "earlyBookings":
      return stats.earlyBookingCount ?? 0;
    case "lateEvents":
      return stats.lateEventCount ?? 0;
    case "packs":
      return stats.packsBoughtCount ?? 0;
    case "invites":
      return stats.invitesJoinedCount ?? 0;
    case "distanceKm":
      return stats.distanceKm ?? 0;
    case "hexes":
      return stats.hexesHeldCount ?? 0;
    case "streakDays":
      return stats.longestStreakDays ?? 0;
    case "founding":
      return stats.foundingMembershipCount ?? 0;
    case "likesReceived":
      return stats.likesReceivedCount ?? 0;
    case "polls":
      return stats.pollsCreatedCount ?? 0;
    case "scoresEntered":
      return stats.scoresEnteredCount ?? 0;
    case "ratingGain":
      return stats.biggestRatingGain ?? 0;
    case "sports":
      return stats.sportsPlayedCount ?? 0;
    case "fullHouses":
      return stats.fullHouseCount ?? 0;
    case "sessionInvites":
      return stats.invitesAcceptedCount ?? 0;
    case "splitsSettled":
      return stats.splitsSettledCount ?? 0;
    case "topups":
      return stats.topupCount ?? 0;
    case "hiiveMoneyPayments":
      return stats.hiiveMoneyPaymentCount ?? 0;
  }
}

export interface StickerProgress {
  current: number;
  remaining: number;
  percentage: number;
  unlocked: boolean;
}

export function getStickerProgress(
  sticker: WalletSticker,
  stats: WalletStats,
): StickerProgress {
  const current = metricValue(stats, sticker.metric);
  const unlocked = current >= sticker.threshold;

  return {
    current,
    remaining: Math.max(sticker.threshold - current, 0),
    percentage: Math.min(Math.round((current / sticker.threshold) * 100), 100),
    unlocked,
  };
}

export function stickerRequirement(sticker: WalletSticker): string {
  const noun = METRIC_LABEL[sticker.metric][sticker.threshold === 1 ? 0 : 1];
  return `${sticker.threshold} ${noun}`;
}
